<template>
  <div class="user-wrapper w-100">
    <profile-header></profile-header>
    <div class="user-content">
      <div class="responsive-container">
        <Loader v-if="loading" />
        <div class="my-auto w-100 main-area" v-else-if="event">
          <div class="profile-detail">
            <TitleContainer :title="'一覧に戻る'" />

            <!-- Event Card (Same design as list) -->
            <EventCard
              :event="event"
              :clickable="false"
              :use-memoize="false"
              :show-mobile-layout="false"
              @buttonClick="showEventModal"
            />

            <!-- Event Summary, Details and Apply Button in single card -->
            <div
              class="container profile-detail-block event-detail-block"
              v-if="event.event_summary || event.event_detail"
            >
              <div class="event-content-section p-10">
                <div
                  class="event-summary-content"
                  v-if="event.event_summary"
                  v-html="event.event_summary"
                ></div>
                <div
                  class="event-separator"
                  v-if="event.event_summary && event.event_detail"
                ></div>
                <div
                  class="event-details-content"
                  v-if="event.event_detail"
                  v-html="event.event_detail"
                ></div>
                <!-- Apply Button -->
                <div class="event-apply-section">
                  <div class="button-container">
                    <button
                      type="button"
                      :class="checkIfApplied(event)"
                      :disabled="checkToDisable(event)"
                      @click="showEventModal(event)"
                      class="event-button"
                    >
                      {{ checkApplied(event) }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Event Modal (Same as Event.vue) -->
            <b-modal
              id="eventModal"
              ref="eventModal"
              size="lg"
              :no-close-on-esc="true"
              :no-close-on-backdrop="true"
              @shown="addEventListeners"
              @hide="removeEventListeners"
            >
              <template #modal-header>
                <h5 class="eventTitle">
                  イベント申込み
                </h5>
              </template>
              <p class="event-modal-title">
                開催企業へのキャリア登録が必要です。<br />登録企業として選択してください。
              </p>
              <div
                class="d-flex px-3 py-2 flex-wrap flex-column align-items-start align-content-center"
              >
                <div class="col-md-6 p-0">
                  <div
                    class="col border py-1"
                    v-for="(company, index) in event.companies"
                    v-bind:key="company._id"
                  >
                    <div class="d-flex justify-content-between font-16">
                      <div class="d-flex align-items-center">
                        <div style="height: 20px; width: 20px;" class="mr-3">
                          <input
                            class="custom-checkbox"
                            type="checkbox"
                            :id="company._id"
                            :value="company._id"
                            name="other_companies"
                            v-model="profile.other_companies"
                          />
                        </div>
                        <label
                          class="form-check-label"
                          :for="company._id"
                          style="padding-top: 3px;"
                        >
                          {{ company.name }}
                        </label>
                      </div>
                      <div>
                        <i
                          v-b-toggle="`accordino0_${index}`"
                          class="fa fa-chevron-down"
                          aria-hidden="true"
                          style="height: 100%"
                        ></i>
                      </div>
                    </div>
                    <div>
                      <b-collapse
                        class="py-1"
                        v-bind:id="`accordino0_${index}`"
                        accordion="my-accordion"
                        role="tabpanel"
                      >
                        <b-card>
                          <span v-html="urlify(company.short_description)">
                          </span>
                        </b-card>
                      </b-collapse>
                    </div>
                  </div>
                </div>
              </div>
              <template #modal-footer>
                <div
                  class="button-container d-flex flex-column align-items-center"
                >
                  <button
                    title="申込みに進む"
                    class="btn btn-secondary btn-green rounded"
                    :disabled="!checkIfCompany() || eventSubmit"
                    @click="openConfirmModal('event')"
                  >
                    申込みに進む
                  </button>
                  <CancelModal @click="cancel" />
                </div>
              </template>
            </b-modal>

            <!-- Confirmation Modal -->
            <CompaniesConfirmation
              :show="showSharingConfirm"
              @cancel="hideConfirm"
              @confirm="saveEvent"
            >
              <template #content>
                <p
                  style="font-size:24px;text-align:center;font-weight:700;color:#ff0000"
                >
                  直近在籍企業など、あなたの情報を公開したくない企業が含まれていないか十分注意してください。
                </p>
                <ul class="notice-list">
                  <li>
                    あなたのキャリア登録情報が企業へ公開されます。
                  </li>
                  <li>
                    企業側はあなたが他にキャリア登録している企業を知ることはできません。
                  </li>
                  <li>
                    許諾無しにあなたがキャリア登録していない企業へ情報が公開されることはありません。
                  </li>
                </ul>
              </template>
              <template #confirm-text>
                注意事項に同意してキャリア登録し<br />
                <span class="font-bold font-14" style="font-size: 18px; width:">
                  申込む
                </span>
              </template>
              <template #confirm-text-mobile>
                <span style="font-size:8px"
                  >注意事項に同意してキャリア登録し、</span
                ><br />
                <span style="font-weight: bold;">申込む</span>
              </template>
            </CompaniesConfirmation>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from '@/config/api';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import swal from 'sweetalert';
import logo from '../assets/Group300.png';
import CompaniesConfirmation from './CompaniesConfirmation.vue';
import modalEventListener from '../mixins/modalEventListener';
import eventHelpers from '../mixins/eventHelpers';
import EventCard from './EventCard.vue';
import CancelModal from './CancelModal.vue';
import ProfileHeader from './header.vue';
import TitleContainer from './TitleContainer.vue';
import Loader from './Loader.vue';

export default {
  mixins: [modalEventListener, eventHelpers],
  name: 'EventDetail',
  components: {
    CompaniesConfirmation,
    CancelModal,
    ProfileHeader,
    TitleContainer,
    EventCard,
    Loader,
  },
  data() {
    return {
      event: null,
      tempSharingData: {},
      showSharingConfirm: false,
      eventSubmit: false,
      server_errors: {},
      loading: true,
    };
  },
  computed: {
    ...mapGetters({
      profile: 'getProfile',
      allEvents: 'getAllEvents',
    }),
  },
  methods: {
    ...mapActions({
      setProfile: 'setProfile',
      setAllEvents: 'setAllEvents',
    }),
    goBack() {
      this.$router.go(-1);
    },
    checkIfCompany() {
      let companies = [];
      if (this.event && JSON.stringify(this.event) != '{}') {
        companies = this.event.companies.map((item) => {
          return item._id;
        });
      }
      const found = companies.some((c) =>
        this.profile.other_companies.includes(c)
      );
      return found;
    },
    async saveEvent() {
      try {
        let data = {
          event_id: this.event._id,
          other_companies: this.profile.other_companies,
        };
        await this.$auth.getToken();
        this.eventSubmit = true;
        let user = await this.axios({
          method: 'post',
          data,
          url: Api.url(Api.endpoints.event),
        });
        this.profile.events = user.data.data.events;
        this.$refs.eventModal.hide();
        this.showSharingConfirm = false;
        this.eventSubmit = false;
        let content = document.createElement('span');
        content.innerHTML = `<p style="font-weight:bold">イベント申込みが完了しました。</p>`;
        await new Promise((resolve) => {
          setTimeout(async () => {
            await swal({
              title: '',
              content: content,
              className: 'custom-swal4',
            //   icon: logo,
              buttons: {
                ok: 'OK',
              },
            });
            resolve();
          }, 100);
        });
      } catch (error) {
        if (
          error &&
          error.response &&
          error.response.status === 403 &&
          error.response.data.data
        ) {
          swal('', error.response.data.data.error_description, 'error');
        } else if (
          (error.response && error.response.status === 503) ||
          (error.response && error.response.status === 401)
        ) {
          this.$router.push({ name: 'Logout' });
        } else {
          swal('', 'Something Went Wrong', 'error');
        }
        this.showSharingConfirm = false;
        this.$refs.eventModal.hide();
      }
      this.isEventEdit = false;
    },
    hideConfirm() {
      this.showSharingConfirm = false;
    },
    showEventModal() {
      this.tempSharingData.other_companies = this.profile.other_companies;
      this.$refs.eventModal.show();
    },
    cancel() {
      this.$refs.eventModal.hide();
      this.profile.other_companies = this.tempSharingData.other_companies;
      this.clearErrors();
    },
    clearErrors() {
      this.server_errors = {};
    },
    openConfirmModal() {
      this.showSharingConfirm = true;
    },
  },
  async created() {
    this.loading = true;
    try {
      await this.setProfile();
      await this.setAllEvents();

      const eventId = this.$route.params.id;
      const foundEvent = this.allEvents.find((e) => e._id === eventId);
      if (foundEvent) {
        this.event = foundEvent;
      } else {
        // Event not found, redirect back
        this.$router.push({ name: 'home' });
      }
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
.profile-detail-block {
  background-color: #ffffff;
}

.event-detail-block {
  margin-top: 15px;
  background-color: #ffffff !important;
  border-radius: 10px;
  box-shadow: 4px 4px 4px 4px #0000000d;
}

.event-content-section {
  display: flex;
  flex-direction: column;
}

.event-summary-content,
.event-details-content {
  max-height: none;
  overflow-y: visible;
  word-break: break-word;
  line-height: 1.6;
  color: #000000;
}

.event-separator {
  width: 100%;
  height: 1px;
  background-color: #000000;
  margin: 20px 0;
}

.event-apply-section {
  text-align: center;
}

.p-10 {
  padding: 10px;
}

.button-container {
  display: flex;
  justify-content: center;
}

.event-button {
  border-radius: 11px;
  border: #ffffff;
  width: 20%;
  height: 50px;
  font-size: 16px;
  text-align: center;
}

.valid {
  background: #4f46e5;
  color: #ffffff !important;
}

.test {
  background: #219653;
  color: #ffffff !important;
}

.applied {
  background: #f8be57;
  color: #000000 !important;
}

.applied:disabled {
  opacity: 1;
  color: #000000 !important;
}

.test:disabled {
  opacity: 1;
}

.valid:disabled {
  opacity: 1;
  color: #000000 !important;
}

.closed {
  background: #d9d9d9;
  color: #000000 !important;
}

.closed:disabled {
  opacity: 1;
  color: #000000 !important;
}
::v-deep .modal-content {
  box-shadow: 4px 4px 4px 4px #0000000D !important;
  border-radius: 10px !important;
}
/* Modal styles */
::v-deep #eventModal .modal-footer {
  justify-content: center;
  border-top: none;
}

::v-deep #eventModal .modal-header {
    border-bottom: none;
}

input[type='checkbox'] {
  background: #006c6b;
}

.custom-checkbox {
  width: 20px;
  height: 20px;
  color: #006c6b;
}

.eventTitle {
  margin-top: 5px;
  color: black;
  font-weight: 800;
  margin-left: 20px;
}

.event-modal-title {
  margin-top: 60px;
  text-align: center;
  font-weight: 600;
  font-size: 18px;
}

.notice-list {
  font-weight: 700;
  font-size: 13px;
}

.font-16 {
  font-size: 16px !important;
}

@media (max-width: 530px) {
  .event-button {
    height: 40px;
    width: 80%;
    font-size: 16px;
  }
}
</style>
